// ============================================================================
//  FORMS — the three demand-capture forms, and the contract with Netlify Forms.
//
//  Submissions go to Netlify Forms, the backend this site already uses: the
//  form is url-encoded and POSTed to the static stub in public/__forms.html,
//  which is what registers it at deploy time. `form-name` routes it to the
//  right form in the Netlify dashboard.
//
//  `fields` IS the contract. components/forms/FormKit.jsx posts exactly these
//  keys and nothing else, public/__forms.html must declare every one of them,
//  and scripts/verify-content.js fails the build if the two drift — a field
//  missing from the stub is silently dropped by Netlify.
// ============================================================================

export const FORMS = {
  // Form A — "Bring Antara to your city". Demand capture for the next
  // editions. Kept as "interest" so it lands in the same Netlify form as the
  // interest list that came before it.
  interest: {
    fields: ["name", "email", "phone", "city", "country", "drewYou"],
  },
  // Form B — Host Antara (components/forms/HostForm.jsx). The Netlify form
  // keeps the name it was registered under, so past and future submissions
  // stay in one place.
  "private-event": {
    fields: [
      "name", "email", "phone", "city", "preferredDate", "dateFlexible",
      "occasion", "venueType", "guests", "setting", "duration", "budget", "message",
    ],
  },
  // Form C — sponsorship.
  sponsorship: {
    fields: [
      "name", "company", "role", "email", "phone", "website",
      "partnership", "cities", "budget", "message",
    ],
  },
};

// ── Choices ─────────────────────────────────────────────────────────────
export const OCCASIONS = [
  "Wedding function",
  "Corporate",
  "Private gathering",
  "Temple / community",
  "Housewarming",
  "Other",
];

export const VENUE_TYPES = [
  "Banquet hall",
  "Farmhouse",
  "Home",
  "Outdoor / garden",
  "Auditorium",
  "Hotel",
  "Other",
];

export const GUEST_RANGES = [
  "Under 50",
  "50–100",
  "100–250",
  "250–500",
  "500–1,000",
  "More than 1,000",
  "Not sure yet",
];

export const SETTINGS = ["Indoor", "Outdoor", "Not sure yet"];

export const DURATIONS = [
  "About 1 hour",
  "1–2 hours",
  "2–3 hours",
  "More than 3 hours",
  "Not sure yet",
];

// Shared by the private-event and sponsorship forms. Always optional, and
// always ends with a way out of naming a number.
export const BUDGET_RANGES = [
  "Under ₹1 lakh",
  "₹1–3 lakh",
  "₹3–5 lakh",
  "₹5–10 lakh",
  "₹10–25 lakh",
  "More than ₹25 lakh",
  "Prefer to discuss",
];

export const PARTNERSHIPS = [
  "Title sponsor",
  "Venue partner",
  "Hospitality",
  "Media / PR",
  "In-kind / product",
  "Other",
];

// ── Validation ──────────────────────────────────────────────────────────
// Messages are written for a person on a phone, not for a log file.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const check = {
  required: (label) => (v) =>
    (Array.isArray(v) ? v.length : String(v ?? "").trim()) ? null : `Please add your ${label}.`,
  choose: (label) => (v) =>
    (Array.isArray(v) ? v.length : String(v ?? "").trim()) ? null : `Please choose ${label}.`,
  email: (v) => {
    const s = String(v ?? "").trim();
    if (!s) return "Please add your email, so we can write back.";
    return EMAIL.test(s) ? null : "That email doesn't look complete — check for a typo?";
  },
  // 7–15 digits once spaces, dashes, brackets and a leading + are ignored:
  // enough for an Indian mobile with or without +91, or an overseas number.
  phone: ({ optional = false } = {}) => (v) => {
    const s = String(v ?? "").trim();
    if (!s) return optional ? null : "Please add a phone number, so we can call you back.";
    const digits = s.replace(/[\s\-().+]/g, "");
    if (!/^\d+$/.test(digits)) return "A phone number should be digits only — e.g. +91 98765 43210.";
    return digits.length >= 7 && digits.length <= 15 ? null : "That number looks too short or too long.";
  },
  url: (v) => {
    const s = String(v ?? "").trim();
    if (!s) return null;
    return /^(https?:\/\/)?[^\s/$.?#]+\.[^\s]{2,}$/i.test(s) ? null : "That website doesn't look right — e.g. yourbrand.com.";
  },
};

/** Run a { field: [rule, …] } map over the values; returns { field: message }. */
export function validate(rules, values) {
  const errors = {};
  for (const [field, list] of Object.entries(rules)) {
    for (const rule of list) {
      const msg = rule(values[field]);
      if (msg) { errors[field] = msg; break; }
    }
  }
  return errors;
}
